import { createContext, useEffect, useCallback, useState, useMemo } from "react";
import type { MutableRefObject } from "react";
import type { SideMenuContextProps, SideMenuContextProviderProps } from "./SideMenuContext.types";

export const SideMenuContext = createContext({} as SideMenuContextProps);

export const SideMenuContextProvider = ({ children }: SideMenuContextProviderProps) => {
  const [headings, setHeadings] = useState<MutableRefObject<HTMLHeadingElement>[]>([]);
  const [activeHeading, setActiveHeading] = useState<MutableRefObject<HTMLHeadingElement>>();

  const addHeading = useCallback(
    (heading: MutableRefObject<HTMLHeadingElement>) => setHeadings((prevHeadings) => [...prevHeadings, heading]),
    []
  );

  const removeHeading = useCallback(
    (heading: MutableRefObject<HTMLHeadingElement>) =>
      setHeadings((prevHeadings) => prevHeadings.filter((prevHeading) => prevHeading !== heading)),
    []
  );

  useEffect(() => {
    const setBestHeading = (headings: MutableRefObject<HTMLHeadingElement>[]) => {
      const viewportThreshold = window.scrollY + window.innerHeight * 0.25;
      const headingsAbove = headings.filter((heading) => heading?.current?.offsetTop < viewportThreshold);

      if (headingsAbove && headingsAbove.length > 0) {
        setActiveHeading(headingsAbove[headingsAbove.length - 1]);
        return;
      }
      setActiveHeading(headings[0]);
    };
    setBestHeading(headings);

    const observerCallback = () => {
      setBestHeading(headings);
    };

    const observer = new IntersectionObserver(observerCallback, { threshold: 0, rootMargin: "0% 0% -75% 0%" });
    headings.forEach((heading) => heading.current && observer.observe(heading.current));

    return () => {
      observer.disconnect();
    };
  }, [headings]);

  const value = useMemo(
    () => ({
      headings,
      activeHeading,
      addHeading,
      removeHeading,
    }),
    [headings, activeHeading, addHeading, removeHeading]
  );

  return <SideMenuContext.Provider value={value}>{children}</SideMenuContext.Provider>;
};
